import { useMemo, useCallback } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { SheduleTableProps, SteriliserRoutingRowType } from './Scheduler.types'
import {
  Box,
  Button,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  TextField,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import {
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium'

const dataConfig = {
  required: ['steriliserTypeId', 'basketFamilyId', 'lines'],
  optional: [],
}

type SteriliserRoutingTableProps =
  SheduleTableProps<SteriliserRoutingRowType> & {
    onAddRow?: () => void
    availableLines?: string[]
    onDeleteRows?: (rows: SteriliserRoutingRowType[]) => Promise<void>
  }

// Display the comma-separated `lines` field as chips. Mirrors the
// synchronized-lines tab so the two SORs feel identical.
const LinesCell = ({ value }: GridRenderCellParams) => {
  if (!value) return null
  const lines = String(value)
    .split(',')
    .map((l) => l.trim())
    .filter(Boolean)
  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ py: 0.5 }}
    >
      {lines.map((line) => (
        <Chip key={line} label={line} size="small" />
      ))}
    </Stack>
  )
}

// Autocomplete-multi editor backed by the API-supplied availableLines list.
const LinesEditCell = ({
  id,
  value,
  field,
  availableLines,
}: GridRenderEditCellParams & { availableLines: string[] }) => {
  const apiRef = useGridApiContext()

  const currentLines = value
    ? String(value)
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
    : []

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string[]) => {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: newValue.join(', '),
      })
    },
    [apiRef, id, field]
  )

  return (
    <Autocomplete
      multiple
      value={currentLines}
      onChange={handleChange}
      options={availableLines}
      disableCloseOnSelect
      size="small"
      sx={{ width: '100%', minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Select lines..."
          autoFocus
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
          />
        ))
      }
    />
  )
}

export const SchedulerSteriliserRoutingTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  onAddRow,
  availableLines = [],
  onDeleteRows,
  isDeleting,
  readOnly = false,
}: SteriliserRoutingTableProps) => {
  const handleDataUpdate = (
    changedRows: Record<number, SteriliserRoutingRowType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Steriliser Type',
        field: 'steriliserTypeId',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Basket Family',
        field: 'basketFamilyId',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Lines',
        field: 'lines',
        type: 'string',
        flex: 2,
        editable: true,
        renderCell: (params: GridRenderCellParams) => <LinesCell {...params} />,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <LinesEditCell {...params} availableLines={availableLines} />
        ),
      },
    ],
    [availableLines]
  )

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddRow}
          >
            Add Row
          </Button>
          {availableLines.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Click on the Lines cell to select from {availableLines.length}{' '}
              available lines
            </Typography>
          )}
        </Box>
      )}
      <BaseTable
        // Each user's saved view of this table (Save as my default).
        layoutKey="SteriliserRouting"
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        readOnly={readOnly}
        downloadFileName="SchedulerSteriliserRouting"
        onDeleteRows={readOnly ? undefined : onDeleteRows}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
